import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import { ownersApi } from '../../services/mybudgetApi';
import { OwnerWithDebt } from '../../types';
import { Card } from '../../components/shared/Card';
import { Loading } from '../../components/shared/Loading';
import { ActionButton } from '../../components/shared/ActionButton';
import { Input } from '../../components/shared/Input';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { useToaster } from '../../components/shared/Toaster';
import { formatCurrency } from '../../constants';

export function OwnersPage() {
  const [owners, setOwners] = useState<OwnerWithDebt[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OwnerWithDebt | null>(null);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToaster();

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOwners(await ownersApi.list(true));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalDebt = owners.reduce((s, o) => s + o.totalRemaining, 0);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', phone: '', email: '', notes: '' });
    setModalOpen(true);
  };

  const openEdit = (owner: OwnerWithDebt) => {
    setEditing(owner);
    setForm({
      name: owner.name,
      phone: owner.phone ?? '',
      email: owner.email ?? '',
      notes: owner.notes ?? '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone || undefined,
        email: form.email || undefined,
        notes: form.notes || undefined,
      };
      if (editing) {
        await ownersApi.update(editing.id, payload);
        addToast({ type: 'success', message: 'Credor atualizado!' });
      } else {
        await ownersApi.create(payload);
        addToast({ type: 'success', message: 'Credor criado!' });
      }
      setModalOpen(false);
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao salvar',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (owner: OwnerWithDebt) => {
    if (!window.confirm(`Excluir o credor "${owner.name}"?`)) return;
    try {
      await ownersApi.delete(owner.id);
      addToast({ type: 'success', message: 'Credor removido' });
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao remover',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-100">Credores</h2>
          <p className="text-sm text-gray-400">
            Pessoas e empresas a quem você deve — por projeto
          </p>
        </div>
        <ActionButton onClick={openCreate}>
          <AddIcon fontSize="small" />
          Novo credor
        </ActionButton>
      </div>

      {owners.length > 0 && (
        <Card>
          <div className="flex items-center gap-3">
            <PersonIcon className="text-amber-400" />
            <div>
              <p className="text-sm text-gray-400">Total em aberto com credores</p>
              <p className="text-2xl font-bold text-amber-400">
                {formatCurrency(totalDebt)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <Loading />
      ) : owners.length === 0 ? (
        <Card>
          <p className="text-center text-gray-500">
            Nenhum credor cadastrado. Exemplo: João — pedreiro, Maria — eletricista.
          </p>
        </Card>
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-border bg-dark-bg/40 text-left text-gray-400">
                  <th className="px-5 py-3">Credor</th>
                  <th className="px-5 py-3 text-center">Projetos</th>
                  <th className="px-5 py-3 text-right">Total</th>
                  <th className="px-5 py-3 text-right">Pago</th>
                  <th className="px-5 py-3 text-right">Em aberto</th>
                  <th className="px-5 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {owners.map((owner) => (
                  <tr
                    key={owner.id}
                    className="border-b border-dark-border/50 hover:bg-dark-bg/30"
                  >
                    <td className="px-5 py-4">
                      <p className="font-medium text-gray-100">{owner.name}</p>
                      {(owner.phone || owner.email) && (
                        <p className="text-xs text-gray-500">
                          {[owner.phone, owner.email].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4 text-center text-gray-300">
                      {owner.projectCount}
                      {owner.activeProjectCount > 0 && (
                        <span className="ml-1 text-xs text-amber-400">
                          ({owner.activeProjectCount} ativo
                          {owner.activeProjectCount !== 1 ? 's' : ''})
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right text-gray-300">
                      {formatCurrency(owner.totalCommitted)}
                    </td>
                    <td className="px-5 py-4 text-right text-income">
                      {formatCurrency(owner.totalPaid)}
                    </td>
                    <td className="px-5 py-4 text-right font-semibold text-amber-400">
                      {formatCurrency(owner.totalRemaining)}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/projects?ownerId=${owner.id}`}
                          className="rounded-lg px-2 py-1 text-xs text-link hover:bg-dark-border"
                        >
                          Projetos
                        </Link>
                        <button
                          onClick={() => openEdit(owner)}
                          className="text-gray-500 hover:text-accent"
                        >
                          <EditIcon fontSize="small" />
                        </button>
                        <button
                          onClick={() => handleDelete(owner)}
                          className="text-gray-500 hover:text-red-400"
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar credor' : 'Novo credor'}
        footer={
          <ModalActions
            onCancel={() => setModalOpen(false)}
            onConfirm={handleSave}
            loading={saving}
          />
        }
      >
        <div className="space-y-4">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <Input
            label="Telefone (opcional)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Input
            label="E-mail (opcional)"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label="Observações (opcional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
}
